/**
 * Utility functions for GIS feature interactions in Cesium
 */

/**
 * Sets opacity for GIS features to highlight selection
 * @param viewer - The Cesium viewer instance
 * @param selectedIdeaId - The ID of the selected idea/feature (optional)
 */
export function setGisFeaturesOpacity(viewer: any, selectedIdeaId?: string) {
  if (!viewer || !viewer.dataSources) return;

  // Find the GIS data source
  const gisDataSource = viewer.dataSources.getByName("gisDataSource")[0];
  if (!gisDataSource) return;

  const isAnyIdeaSelected = !!selectedIdeaId;

  gisDataSource.entities.values.forEach((entity: any) => {
    const properties = entity.properties?.getValue();
    const entityIdeaId = properties?.ideaId;

    // Only apply opacity changes to features that have an ideaId property
    if (!entityIdeaId) {
      return; // Skip features without ideaId
    }

    // Determine if this entity should be highlighted or dimmed
    const isSelectedEntity = selectedIdeaId && entityIdeaId === selectedIdeaId;
    const shouldBeDimmed = isAnyIdeaSelected && !isSelectedEntity;

    if (entity.polygon) {
      if (entity.polygon.material && entity.polygon.material.color) {
        const currentColor = entity.polygon.material.color.getValue();
        if (currentColor) {
          if (isSelectedEntity) {
            // Highlight selected polygon
            entity.polygon.material.color = currentColor.withAlpha(0.7);
          } else if (shouldBeDimmed) {
            // Dim other polygons when something is selected
            entity.polygon.material.color = currentColor.withAlpha(0.3);
          }
          // If no specific action needed, maintain current opacity
        }
      }
    }

    if (entity.polyline) {
      if (entity.polyline.material && entity.polyline.material.color) {
        const currentColor = entity.polyline.material.color.getValue();
        if (currentColor) {
          if (isSelectedEntity) {
            // Highlight selected polyline
            entity.polyline.material.color = currentColor.withAlpha(1.0);
          } else if (shouldBeDimmed) {
            // Dim other polylines when something is selected
            entity.polyline.material.color = currentColor.withAlpha(0.3);
          }
          // If no specific action needed, maintain current opacity
        }
      }
    }

    if (entity.point) {
      if (entity.point.color) {
        const currentColor = entity.point.color.getValue();
        if (currentColor) {
          if (isSelectedEntity) {
            // Highlight selected point
            entity.point.color = currentColor.withAlpha(1.0);
          } else if (shouldBeDimmed) {
            // Dim other points when something is selected
            entity.point.color = currentColor.withAlpha(0.3);
          }
          // If no specific action needed, maintain current opacity
        }
      }
    }
  });
}

/**
 * Filters out point features with the specified ideaId from the GIS data source
 * This is used when showing a custom pin for an idea to avoid duplicate markers
 * @param viewer - The Cesium viewer instance
 * @param ideaIdToHide - The ID of the idea whose point should be hidden
 */
export function filterGisPointFeatures(viewer: any, ideaIdToHide?: string) {
  if (!viewer || !viewer.dataSources) return;

  // Find the GIS data source
  const gisDataSource = viewer.dataSources.getByName("gisDataSource")[0];
  if (!gisDataSource) return;

  gisDataSource.entities.values.forEach((entity: any) => {
    const properties = entity.properties?.getValue();

    // Only handle point entities
    if (entity.point && properties?.ideaId) {
      if (ideaIdToHide && properties.ideaId === ideaIdToHide) {
        // Hide the point entity that matches the idea being pinned
        entity.show = false;
      } else {
        // Show all other point entities
        entity.show = true;
      }
    }
  });
}

/**
 * Resets all GIS point features to be visible
 * @param viewer - The Cesium viewer instance
 */
export function showAllGisPointFeatures(viewer: any) {
  filterGisPointFeatures(viewer); // Called without ideaIdToHide shows all points
}
